/**
 * placementBus — one-way seam from accountStore's Kalshi write paths to the
 * per-ticker open-order lane (`useUserKalshiPOS`).
 *
 * Why it exists: the ladder, GameTable and Game Detail own-order markers read
 * `useUserKalshiPOS`, which is fed only by the relay's `pos` frames and the
 * `/me/snapshot` poll. accountStore already projects a venue-ACCEPTED resting
 * order into its own `openOrders` fence the moment the relay answers, but
 * that projection never reached this lane — so the trader heard the accept
 * tone and then watched the board for the next relay frame before the order
 * appeared. The store publishes here; the hook registers its listener at
 * module init. accountStore cannot import the hook directly (the hook already
 * imports from accountStore), hence the bus.
 *
 * Contract:
 *   • `emitPlacement` carries ONLY explicit venue-accepted RESTING evidence —
 *     the same gate accountStore uses for its own fence. Rejections, immediate
 *     fills and market orders never reach it.
 *   • `emitPlacementRemoved` fires when the store learns the order is no
 *     longer resting from a source it trusts (a cancel the venue accepted, a
 *     fill that drained it, the cancel lifecycle confirming absence), so a
 *     projection cannot outlive the order it stood in for.
 *   • No listener registered (no POS consumer loaded) ⇒ harmless no-ops.
 *   • `queuePosition` is never carried: a projection must not invent queue
 *     evidence; the lane renders it as an honest unknown until the venue
 *     snapshot carries the row.
 */

/** The projected order row — structurally assignable to UserOrderSlice. */
export interface PlacementEvent {
  ticker: string;
  orderId: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  priceCents: number;
  remainingCount: number;
}

export interface PlacementListener {
  placed: (event: PlacementEvent) => void;
  removed: (orderId: string) => void;
}

let listener: PlacementListener | null = null;

/** Register (or clear, with null) THE projection listener. Last write wins —
 *  there is exactly one consumer lane by design. */
export function setPlacementListener(fn: PlacementListener | null): void {
  listener = fn;
}

/** Publish a venue-accepted resting placement. Safe with no listener. */
export function emitPlacement(event: PlacementEvent): void {
  listener?.placed(event);
}

/** Publish that an order is known to have left the book. Safe with no listener. */
export function emitPlacementRemoved(orderId: string): void {
  listener?.removed(orderId);
}
